import type { PropsWithChildren } from 'react'
import { useCallback, useEffect } from 'react'

import type { SubmitErrorHandler } from 'react-hook-form'
import { FormProvider, useForm } from 'react-hook-form'

import { useToast } from '@/components/ui/toast.tsx'
import { useMessageEditor } from '@/modules/message/components/editor/message-editor-context'
import { nodes } from '@/modules/message/components/placeholder/placeholder-editor'

import type { MessageFormValues } from './schema'

interface MessageEditorFormProps extends PropsWithChildren {
  addedCategoryId?: number
}

export const MessageEditorForm = (props: MessageEditorFormProps) => {
  const { children } = props
  const { defaultValues, schema, editor, onSubmit, hasPlaceholders } =
    useMessageEditor()

  const toast = useToast()

  const form = useForm<MessageFormValues>({ defaultValues, resolver: schema })

  useEffect(() => {
    if (props.addedCategoryId) {
      form.setValue('category', props.addedCategoryId)
    }
  }, [form, props.addedCategoryId])

  useEffect(() => {
    if (defaultValues) {
      form.reset(defaultValues)
    }
  }, [defaultValues, editor, form])

  const handleSave = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (state: any) => {
      if (hasPlaceholders && (!state.fallback || state.fallback === '')) {
        form.setError(
          'fallback',
          { message: 'Please enter a fallback name' },
          { shouldFocus: true },
        )

        return
      } else {
        form.clearErrors('fallback')
      }

      if (state.price && state.price > 200) {
        form.setError(
          'price',
          { message: 'Maximum $200 USD' },
          { shouldFocus: true },
        )

        return
      }

      if (state.price && state.price < 3) {
        form.setError(
          'price',
          { message: 'Minimum $3 USD' },
          { shouldFocus: true },
        )

        return
      }

      onSubmit({
        ...state,
        text: nodes.stringify(editor.children),
      })
    },
    [hasPlaceholders, onSubmit, editor.children, form],
  )

  const handleError: SubmitErrorHandler<MessageFormValues> = useCallback(
    (errors) => {
      console.warn('Message Editor has errors', errors)
      const description = Object.values(errors)
        .map((error) => error?.message)
        .join('\n')

      toast.error('Please fix the errors in the form', { description })
    },
    [toast],
  )

  return (
    <FormProvider {...form}>
      <form onSubmit={form.handleSubmit(handleSave, handleError)}>
        {children}
      </form>
    </FormProvider>
  )
}
